'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Timer } from 'lucide-react';
import { TournamentFormat } from '@/types/tournament.model';
import { getTournamentFormatLabel } from '@/utils/tournament-format';
import { cn } from '@/lib/utils';
import { FieldHelp } from '@/features/guided-help';

export interface TournamentFormatFieldProps {
  value: TournamentFormat;
  onChange: (value: TournamentFormat) => void;
  disabled?: boolean;
}

/** Formats not selectable for now (disabled in the UI). Empty: SHORT, AMERICAN and LONG are enabled. */
const TEMPORARILY_DISABLED_FORMATS = new Set<TournamentFormat>([]);

const OPTIONS: { value: TournamentFormat; description: string }[] = [
  {
    value: TournamentFormat.SHORT,
    description: 'Ideal para torneos de fin de semana.',
  },
  {
    value: TournamentFormat.AMERICAN,
    description: 'Torneos de 1 día con pareja fija rotativa.',
  },
  {
    value: TournamentFormat.LONG,
    description: 'Torneo para modo liga: jornadas semanales con disponibilidad y propuesta.',
  },
];

export function TournamentFormatField({
  value,
  onChange,
  disabled = false,
}: TournamentFormatFieldProps) {
  return (
    <Card data-guide-id="wizard.fixture.format">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Timer className="h-5 w-5" />
          Tipo de torneo
          <FieldHelp topicId="wizard.fixture.format" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Define la duración y la dinámica general del torneo (por ejemplo, menos
          rondas en el formato corto). Es independiente de si hay fase de grupos o
          solo eliminatorias.
        </p>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={value}
          onValueChange={(v) => onChange(v as TournamentFormat)}
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          disabled={disabled}
        >
          {OPTIONS.map((opt) => {
            const optDisabled =
              disabled || TEMPORARILY_DISABLED_FORMATS.has(opt.value);
            const isSelected = value === opt.value;
            return (
              <div
                key={opt.value}
                role="button"
                tabIndex={optDisabled ? -1 : 0}
                aria-pressed={isSelected}
                aria-disabled={optDisabled}
                onClick={() => !optDisabled && onChange(opt.value)}
                onKeyDown={(e) => {
                  if (optDisabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(opt.value);
                  }
                }}
                className={cn(
                  'flex flex-col gap-2 rounded-lg border-2 p-4 transition-all',
                  isSelected && !optDisabled
                    ? 'border-blue-500 bg-blue-50'
                    : optDisabled
                      ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                      : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300',
                )}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={opt.value}
                    id={`format-${opt.value}`}
                    disabled={optDisabled}
                    tabIndex={-1}
                    className={
                      isSelected && !optDisabled
                        ? 'border-blue-600 text-blue-600 data-[state=checked]:border-blue-600'
                        : undefined
                    }
                  />
                  <Label
                    htmlFor={`format-${opt.value}`}
                    className={cn(
                      'font-medium',
                      optDisabled
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer',
                      isSelected && !optDisabled && 'text-blue-700',
                    )}
                  >
                    {getTournamentFormatLabel(opt.value)}
                  </Label>
                </div>
                <p
                  className={cn(
                    'pl-6 text-xs',
                    isSelected && !optDisabled
                      ? 'text-blue-700/85'
                      : 'text-muted-foreground',
                  )}
                >
                  {opt.description}
                </p>
              </div>
            );
          })}
        </RadioGroup>
      </CardContent>
    </Card>
  );
}
